import React, { useCallback, useEffect, useMemo, useState } from "react";
import usePersistentFilters from "../Common/usePersistentFilters";
import { Modal, Dropdown, Nav, Tab } from "react-bootstrap";
import Swal from "sweetalert2";
import api, { getFileUrl } from "../../../api";
import usePermission from "../../../hooks/usePermission";
import PageTitle from "../../layouts/PageTitle";
import NCRMonitor from "../QC/NCRMonitor";

const CATEGORIES = [
 "Audit Observation",
 "Work Quality Issue",
 "Non-Compliance (as per standards)",
 "Non-Conformance (NCR)",
 "Work Check Request",
 "Document Issue (Drawings / Bills / TDS / Manuals)",
 "General Issue / Query",
 "False Alarm",
 "Other",
];

const PRIORITIES = ["Low", "Medium", "High", "Critical"];
const STATUS_FILTERS = ["", "Open", "Assigned", "In Progress", "Resolved", "Closed", "Reopened", "False Alarm", "Need Clarification"];
const ENGINEER_STATUSES = ["In Progress", "Resolved", "Need Clarification"];
const CLIENT_FINAL_STATUSES = ["Closed", "Reopened", "False Alarm"];

const emptyForm = {
 project: "",
 workOrder: "",
 category: "General Issue / Query",
 priority: "Medium",
 title: "",
 description: "",
 location: "",
 assignedTo: "",
};

const emptyAction = {
 status: "In Progress",
 comment: "",
};

const badgeClass = (value) => {
 const normalized = (value || "").toLowerCase();
 if (["critical", "reopened", "high"].includes(normalized)) return "badge badge-danger";
 if (["medium", "need clarification"].includes(normalized)) return "badge badge-warning";
 if (["resolved", "closed", "low"].includes(normalized)) return "badge badge-success";
 if (["assigned", "in progress"].includes(normalized)) return "badge badge-info";
 if (["open"].includes(normalized)) return "badge badge-primary";
 return "badge badge-light";
};

const getRoleFlags = (role) => {
 const normalized = (role || "").toLowerCase().replace(/\s/g, "");
 return {
 isClient: normalized === "client" || normalized === "po",
 isEngineer: normalized.includes("engineer"),
 isManager: ["admin", "pm", "manager", "projectmanager"].includes(normalized),
 };
};

const toArray = (payload) => payload?.data?.data || payload?.data || [];

const AttachmentItem = ({ file, index }) => {
 const isImage = file.mimeType?.startsWith("image/");
 const isPDF = file.mimeType === "application/pdf";
 const url = getFileUrl(file.fileUrl);
 const sizeKb = file.size ? (file.size / 1024).toFixed(1) : "0";

 return (
 <div className="d-flex align-items-center border rounded p-2 mb-2 bg-white shadow-sm hover-shadow transition">
 <div className="me-3">
 {isImage ? (
 <i className="fa fa-file-image text-primary fs-4" />
 ) : isPDF ? (
 <i className="fa fa-file-pdf text-danger fs-4" />
 ) : (
 <i className="fa fa-file text-secondary fs-4" />
 )}
 </div>
 <div className="flex-grow-1 overflow-hidden">
 <div className="text-truncate small fw-bold text-dark">{file.fileName || `File ${index + 1}`}</div>
 <div className="text-muted smaller">{sizeKb} KB</div>
 </div>
 <div className="d-flex ms-2">
 <a href={url} target="_blank" rel="noreferrer" className="btn btn-xs btn-info me-1" title="Preview">
 <i className="fa fa-eye" />
 </a>
 <a href={url} download={file.fileName} className="btn btn-xs btn-primary" title="Download">
 <i className="fa fa-download" />
 </a>
 </div>
 </div>
 );
};

function ClientTicketing() {
 const { role } = usePermission();
 const { isClient, isEngineer, isManager } = useMemo(() => getRoleFlags(role), [role]);
 const [tickets, setTickets] = useState([]);
 const [dashboard, setDashboard] = useState(null);
 const [projects, setProjects] = useState([]);
 const [workOrders, setWorkOrders] = useState([]);
 const [engineers, setEngineers] = useState([]);
 const [selectedTicket, setSelectedTicket] = useState(null);
 const [form, setForm] = useState(emptyForm);
 const [filters, setFilters] = usePersistentFilters("client_tickets_filters", { status: "", priority: "", category: "", search: "" });
 const [createFiles, setCreateFiles] = useState([]);
 const [actionFiles, setActionFiles] = useState([]);
 const [actionForm, setActionForm] = useState(emptyAction);
 const [assignUser, setAssignUser] = useState("");
 const [clientStatus, setClientStatus] = useState("Closed");
 const [clientComment, setClientComment] = useState("");
 const [showCreate, setShowCreate] = useState(false);
 const [showDetails, setShowDetails] = useState(false);
 const [loading, setLoading] = useState(false);

 const fetchTickets = useCallback(async () => {
 setLoading(true);
 try {
 const params = new URLSearchParams();
 Object.entries(filters).forEach(([key, value]) => {
 if (value) params.append(key, value);
 });
 const res = await api.get(`/client-tickets?${params.toString()}`);
 setTickets(res.data.data || []);
 } catch (error) {
 Swal.fire("Error", error.response?.data?.message || "Unable to load tickets", "error");
 } finally {
 setLoading(false);
 }
 }, [filters]);

 const fetchDashboard = useCallback(async () => {
 try {
 const res = await api.get("/client-tickets/dashboard");
 setDashboard(res.data.data);
 } catch (error) {
 setDashboard(null);
 }
 }, []);

 const fetchMasters = useCallback(async () => {
 try {
 const [projectRes, workOrderRes, userRes] = await Promise.all([
 api.get("/projects?limit=1000"),
 api.get("/work-orders?limit=1000"),
 api.get("/users?limit=1000"),
 ]);
 setProjects(toArray(projectRes));
 setWorkOrders(toArray(workOrderRes));
 
 // Filter out clients from assignable users
 const allUsers = toArray(userRes);
 const assignableUsers = allUsers.filter(u => {
 const r = (u.role?.name || "").toLowerCase();
 return r !== "client";
 });
 setEngineers(assignableUsers);
 } catch (error) {
 Swal.fire("Warning", "Some dropdown data could not be loaded", "warning");
 }
 }, []);

 useEffect(() => {
 fetchMasters();
 fetchDashboard();
 }, [fetchMasters, fetchDashboard]);

 useEffect(() => {
 fetchTickets();
 }, [fetchTickets]);

 const refreshAll = async () => {
 await Promise.all([fetchTickets(), fetchDashboard()]);
 };

 const openDetails = async (ticket) => {
 try {
 const res = await api.get(`/client-tickets/${ticket._id}`);
 setSelectedTicket(res.data.data);
 setAssignUser(res.data.data?.assignedTo?._id || "");
 setShowDetails(true);
 } catch (error) {
 Swal.fire("Error", error.response?.data?.message || "Unable to open ticket", "error");
 }
 };

 const handleCreate = async (event) => {
 event.preventDefault();
 try {
 const data = new FormData();
 Object.entries(form).forEach(([key, value]) => {
 if (value) data.append(key, value);
 });
 createFiles.forEach((file) => data.append("attachments", file));
 await api.post("/client-tickets", data, { headers: { "Content-Type": "multipart/form-data" } });
 Swal.fire("Created", "Ticket raised successfully", "success");
 setForm(emptyForm);
 setCreateFiles([]);
 setShowCreate(false);
 await refreshAll();
 } catch (error) {
 Swal.fire("Error", error.response?.data?.message || "Unable to create ticket", "error");
 }
 };

 const handleAssign = async () => {
 if (!selectedTicket || !assignUser) return;
 try {
 const res = await api.patch(`/client-tickets/${selectedTicket._id}/assign`, {
 assignedTo: assignUser,
 });
 setSelectedTicket(res.data.data);
 Swal.fire("Assigned", "Engineer assigned successfully", "success");
 await refreshAll();
 } catch (error) {
 Swal.fire("Error", error.response?.data?.message || "Unable to assign ticket", "error");
 }
 };

 const handleEngineerAction = async (event) => {
 event.preventDefault();
 if (!selectedTicket) return;
 try {
 const data = new FormData();
 data.append("status", actionForm.status);
 data.append("comment", actionForm.comment);
 actionFiles.forEach((file) => data.append("attachments", file));
 const res = await api.post(`/client-tickets/${selectedTicket._id}/actions`, data, {
 headers: { "Content-Type": "multipart/form-data" },
 });
 setSelectedTicket(res.data.data);
 setActionForm(emptyAction);
 setActionFiles([]);
 Swal.fire("Updated", "Action submitted successfully", "success");
 await refreshAll();
 } catch (error) {
 Swal.fire("Error", error.response?.data?.message || "Unable to submit action", "error");
 }
 };

 const handleClientStatus = async () => {
 if (!selectedTicket) return;
 try {
 const res = await api.patch(`/client-tickets/${selectedTicket._id}/status`, {
 status: clientStatus,
 comment: clientComment,
 });
 setSelectedTicket(res.data.data);
 setClientComment("");
 Swal.fire("Updated", "Ticket status updated", "success");
 await refreshAll();
 } catch (error) {
 Swal.fire("Error", error.response?.data?.message || "Unable to update ticket status", "error");
 }
 };

 const visibleWorkOrders = useMemo(() => {
 if (!form.project) return workOrders;
 return workOrders.filter((wo) => {
 const projectId = wo.project?._id || wo.project;
 return projectId?.toString() === form.project;
 });
 }, [workOrders, form.project]);

 const stat = dashboard?.totals || {};

 return (
 <>
 <PageTitle activeMenu="Client Tickets" motherMenu="CX360" />

 {/* Tabs: Tickets | NCR Monitor */}
 <Tab.Container defaultActiveKey="tickets">
 <div className="d-flex align-items-center mb-3 gap-3">
 <Nav variant="tabs" className="border-bottom-0">
 <Nav.Item>
 <Nav.Link eventKey="tickets" className="fw-semibold">
 <i className="fas fa-ticket-alt me-1" /> Tickets
 </Nav.Link>
 </Nav.Item>
 <Nav.Item>
 <Nav.Link eventKey="ncr" className="fw-semibold">
 <i className="fas fa-exclamation-triangle me-1 text-danger" /> NCR Monitor
 </Nav.Link>
 </Nav.Item>
 </Nav>
 </div>

 <Tab.Content>
 {/* TAB 1: All Tickets */}
 <Tab.Pane eventKey="tickets">
 <div className="row">
 <div className="col-xl-3 col-sm-6"> <div className="card shadow-sm border-0 bg-primary text-white">
 <div className="card-body">
 <div className="d-flex align-items-center">
 <div className="flex-grow-1">
 <h6 className="text-white opacity-75 mb-1">Total Tickets</h6>
 <h3 className="mb-0 text-white">{stat.total || 0}</h3>
 </div>
 <div className="fs-1 opacity-50"><i className="fa fa-ticket-alt" /></div>
 </div>
 </div>
 </div>
 </div>
 <div className="col-xl-3 col-sm-6">
 <div className="card shadow-sm border-0 bg-warning text-white">
 <div className="card-body">
 <div className="d-flex align-items-center">
 <div className="flex-grow-1">
 <h6 className="text-white opacity-75 mb-1">Open / Active</h6>
 <h3 className="mb-0 text-white">{stat.open || 0}</h3>
 </div>
 <div className="fs-1 opacity-50"><i className="fa fa-clock" /></div>
 </div>
 </div>
 </div>
 </div>
 <div className="col-xl-3 col-sm-6">
 <div className="card shadow-sm border-0 bg-success text-white">
 <div className="card-body">
 <div className="d-flex align-items-center">
 <div className="flex-grow-1">
 <h6 className="text-white opacity-75 mb-1">Closed</h6>
 <h3 className="mb-0 text-white">{stat.closed || 0}</h3>
 </div>
 <div className="fs-1 opacity-50"><i className="fa fa-check-circle" /></div>
 </div>
 </div>
 </div>
 </div>
 <div className="col-xl-3 col-sm-6">
 <div className="card shadow-sm border-0 bg-info text-white">
 <div className="card-body">
 <div className="d-flex align-items-center">
 <div className="flex-grow-1">
 <h6 className="text-white opacity-75 mb-1">Avg Response</h6>
 <h3 className="mb-0 text-white">{stat.avgResponseMs ? `${Math.round(stat.avgResponseMs / 3600000)}h` : "-"}</h3>
 </div>
 <div className="fs-1 opacity-50"><i className="fa fa-bolt" /></div>
 </div>
 </div>
 </div>
 </div>
 </div>

 <div className="card shadow-sm border-0">
 <div className="card-header bg-white py-3 d-flex flex-wrap justify-content-between align-items-center border-bottom">
 <div>
 <h4 className="card-title mb-0">Ticketing & Issue Management</h4>
 <p className="text-muted small mb-0">Track and manage client issues and requests</p>
 </div>
 <button className="btn btn-primary rounded-pill px-4" onClick={() => setShowCreate(true)}>
 <i className="fa fa-plus me-2" /> Raise New Ticket
 </button>
 </div>
 <div className="card-body">
 <div className="row g-3 mb-4 bg-white p-3 rounded border">
 <div className="col-md-3">
 <div className="input-group input-group-sm">
 <span className="input-group-text bg-white border-end-0"><i className="fa fa-search text-muted" /></span>
 <input className="form-control form-control-sm border-start-0 ps-0" placeholder="Search ticket, title..." value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })} />
 </div>
 </div>
 <div className="col-md-3">
 <select className="form-select form-select-sm" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
 {STATUS_FILTERS.map((item) => <option key={item} value={item}>{item || "All Statuses"}</option>)}
 </select>
 </div>
 <div className="col-md-3">
 <select className="form-select form-select-sm" value={filters.priority} onChange={(e) => setFilters({ ...filters, priority: e.target.value })}>
 <option value="">All Priorities</option>
 {PRIORITIES.map((item) => <option key={item}>{item}</option>)}
 </select>
 </div>
 <div className="col-md-3">
 <select className="form-select form-select-sm" value={filters.category} onChange={(e) => setFilters({ ...filters, category: e.target.value })}>
 <option value="">All Categories</option>
 {CATEGORIES.map((item) => <option key={item}>{item}</option>)}
 </select>
 </div>
 </div>

 <div className="table-responsive">
 <table className="table table-hover align-middle">
 <thead className="table-light text-muted small text-uppercase">
 <tr>
 <th style={{ textAlign: "center" }}>Ticket Details</th>
 <th style={{ textAlign: "center" }}>Project / Work Order</th>
 <th style={{ textAlign: "center" }}>Category</th>
 <th style={{ textAlign: "center" }}>Priority</th>
 <th style={{ textAlign: "center" }}>Status</th>
 <th style={{ textAlign: "center" }}>Engineer</th>
 <th style={{ textAlign: "center" }}>Raised On</th>
 <th style={{ textAlign: "center" }}>Action</th>
 </tr>
 </thead>
 <tbody>
 {loading ? (
 <tr><td colSpan="8" className="text-center py-5"><div className="spinner-border text-primary" role="status"></div></td></tr>
 ) : tickets.length ? tickets.map((ticket) => (
 <tr key={ticket._id}>
 <td style={{ textAlign: "center" }}>
 <div className="fw-bold text-dark">{ticket.ticketNo}</div>
 <div className="text-muted small text-truncate" style={{ maxWidth: '200px' }}>{ticket.title || ticket.location}</div>
 </td>
 <td style={{ textAlign: "center" }}>
 <div className="small fw-bold text-primary">{ticket.project?.projectName || "-"}</div>
 <div className="smaller text-muted">{ticket.workOrder?.woNo || "No WO linked"}</div>
 </td>
 <td style={{ textAlign: "center" }}><div className="small">{ticket.category}</div></td>
 <td style={{ textAlign: "center" }}><span className={badgeClass(ticket.priority)}>{ticket.priority}</span></td>
 <td style={{ textAlign: "center" }}><span className={badgeClass(ticket.status)}>{ticket.status}</span></td>
 <td style={{ textAlign: "center" }}>
 {ticket.assignedTo ? (
 <div className="d-flex align-items-center justify-content-center">
 <div className="rounded-circle bg-light text-primary d-flex align-items-center justify-content-center me-2" style={{ width: '28px', height: '28px' }}>
 {ticket.assignedTo.name?.charAt(0)}
 </div>
 <div className="small">{ticket.assignedTo.name}</div>
 </div>
 ) : <span className="text-muted small">Not Assigned</span>}
 </td>
 <td style={{ textAlign: "center" }}><div className="small">{ticket.createdAt ? new Date(ticket.createdAt).toLocaleDateString() : "-"}</div></td>
 <td>
 <div className="d-flex justify-content-center">
 <button className="btn btn-sm btn-light border" onClick={() => openDetails(ticket)}>
 <i className="fa fa-external-link-alt text-primary me-1" /> View Details
 </button>
 </div>
 </td>
 </tr>
 )) : (
 <tr><td colSpan="8" className="text-center py-5 text-muted">No tickets found matching your filters</td></tr>
 )}
 </tbody>
 </table>
 </div>
 </div>
 </div>

 <div className="row">
 <div className="col-xl-4 col-lg-6">
 <div className="card shadow-sm border-0">
 <div className="card-header bg-white border-bottom"><h5 className="card-title mb-0">Issue Category Trends</h5></div>
 <div className="card-body">
 {(dashboard?.byCategory || []).length ? (dashboard?.byCategory || []).map((item) => (
 <div key={item._id} className="mb-3">
 <div className="d-flex justify-content-between mb-1">
 <span className="small fw-bold">{item._id || "Uncategorized"}</span>
 <span className="small text-muted">{item.count} tickets</span>
 </div>
 <div className="progress" style={{ height: '6px' }}>
 <div className="progress-bar bg-primary" style={{ width: `${(item.count / (stat.total || 1)) * 100}%` }}></div>
 </div>
 </div>
 )) : <p className="text-center text-muted">No data available</p>}
 </div>
 </div>
 </div>
 <div className="col-xl-4 col-lg-6">
 <div className="card shadow-sm border-0">
 <div className="card-header bg-white border-bottom"><h5 className="card-title mb-0">Engineer Performance</h5></div>
 <div className="card-body">
 {(dashboard?.engineerWise || []).length ? (dashboard?.engineerWise || []).map((item) => (
 <div key={item._id || "unassigned"} className="mb-3">
 <div className="d-flex justify-content-between mb-1">
 <span className="small fw-bold">{item.engineerName || "Unassigned"}</span>
 <span className="small text-muted">{item.resolved || 0} resolved of {item.total || 0}</span>
 </div>
 <div className="progress" style={{ height: '6px' }}>
 <div className="progress-bar bg-success" style={{ width: `${(item.resolved / (item.total || 1)) * 100}%` }}></div>
 </div>
 </div>
 )) : <p className="text-center text-muted">No data available</p>}
 </div>
 </div>
 </div>
 <div className="col-xl-4 col-lg-12">
 <div className="card shadow-sm border-0">
 <div className="card-header bg-white border-bottom"><h5 className="card-title mb-0">Repeated Issues by Project</h5></div>
 <div className="card-body overflow-auto" style={{ maxHeight: '350px' }}>
 {(dashboard?.repeatedIssues || []).length ? (dashboard?.repeatedIssues || []).map((item, idx) => (
 <div key={idx} className="d-flex align-items-center mb-3 p-2 bg-light rounded border">
 <div className="me-3">
 <span className="badge bg-danger rounded-circle p-2">{item.count}</span>
 </div>
 <div className="flex-grow-1 overflow-hidden">
 <div className="small fw-bold text-dark text-truncate">{item._id.category}</div>
 <div className="smaller text-muted">Repeated in Project ID: {item._id.project?.toString().substring(0, 8)}...</div>
 </div>
 </div>
 )) : <p className="text-center text-muted">No repeated issues detected</p>}
 </div>
 </div>
 </div>
 </div>

 <Modal show={showCreate} onHide={() => setShowCreate(false)} size="lg" centered className="ticket-modal">
 <Modal.Header closeButton className="bg-white"><Modal.Title>Raise Client Ticket</Modal.Title></Modal.Header>
 <form onSubmit={handleCreate}>
 <Modal.Body className="p-4">
 <div className="row g-4">
 <div className="col-md-6">
 <label className="form-label fw-bold small text-uppercase">Project <span className="text-danger">*</span></label>
 <select
                    className="form-select shadow-none border"
                    required
                    value={form.project}
                    onChange={(e) => {
                      const nextProj = e.target.value;
                      const next = { ...form, project: nextProj };
                      if (nextProj && form.workOrder) {
                        const currentWo = workOrders.find((w) => String(w._id) === String(form.workOrder));
                        const currentWoProjId = currentWo?.project?._id || currentWo?.project;
                        if (currentWoProjId && String(currentWoProjId) !== String(nextProj)) {
                          next.workOrder = "";
                        }
                      }
                      setForm(next);
                    }}
                  >
 <option value="">Select project</option>
 {projects.map((project) => <option key={project._id} value={project._id}>{project.projectName}</option>)}
 </select>
 </div>
 <div className="col-md-6">
 <label className="form-label fw-bold small text-uppercase">Work Order</label>
                  <select
                    className="form-select shadow-none border"
                    value={form.workOrder}
                    onChange={(e) => {
                      const nextVal = e.target.value;
                      const next = { ...form, workOrder: nextVal };
                      if (nextVal) {
                        const selectedWo = workOrders.find((w) => String(w._id) === String(nextVal));
                        const autoProjId = selectedWo?.project?._id || selectedWo?.project;
                        if (autoProjId) {
                          next.project = String(autoProjId);
                        }
                      }
                      setForm(next);
                    }}
                  >
                    <option value="">Optional</option>
                    {visibleWorkOrders.map((wo) => (
                      <option key={wo._id} value={wo._id}>
                        {wo.woNo || wo.subject || wo._id}
                        {!form.project && (wo.project?.projectName || wo.project?.name) ? ` (${wo.project.projectName || wo.project.name})` : ""}
                      </option>
                    ))}
                  </select>
 </div>
 <div className="col-md-6">
 <label className="form-label fw-bold small text-uppercase">Ticket Category <span className="text-danger">*</span></label>
 <select className="form-select shadow-none border" required value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
 {CATEGORIES.map((item) => <option key={item}>{item}</option>)}
 </select>
 </div>
 <div className="col-md-6">
 <label className="form-label fw-bold small text-uppercase">Priority <span className="text-danger">*</span></label>
 <select className="form-select shadow-none border" required value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
 {PRIORITIES.map((item) => <option key={item}>{item}</option>)}
 </select>
 </div>
 <div className="col-md-6">
 <label className="form-label fw-bold small text-uppercase">Title</label>
 <input className="form-control shadow-none border" placeholder="Brief subject" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
 </div>
 <div className="col-md-6">
 <label className="form-label fw-bold small text-uppercase">Location / Site Area</label>
 <input className="form-control shadow-none border" placeholder="Specific site area" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
 </div>
 {isManager && (
 <div className="col-md-12">
 <label className="form-label fw-bold small text-uppercase">Assign Engineer</label>
 <select className="form-select shadow-none border" value={form.assignedTo} onChange={(e) => setForm({ ...form, assignedTo: e.target.value })}>
 <option value="">Auto assign from Work Order / Assign later</option>
 {engineers.map((user) => <option key={user._id} value={user._id}>{user.name} - {user.email}</option>)}
 </select>
 </div>
 )}
 <div className="col-md-12">
 <label className="form-label fw-bold small text-uppercase">Description <span className="text-danger">*</span></label>
 <textarea className="form-control shadow-none border" rows="4" required placeholder="Detailed explanation of the issue..." value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
 </div>
 <div className="col-md-12">
 <label className="form-label fw-bold small text-uppercase">Attachments (Photos/Documents)</label>
 <div className="border rounded p-3 bg-white text-center border-dashed">
 <input className="form-control d-none" id="create-files" type="file" multiple onChange={(e) => setCreateFiles(Array.from(e.target.files || []))} />
 <label htmlFor="create-files" className="mb-0 cursor-pointer">
 <i className="fa fa-cloud-upload-alt fs-2 text-primary mb-2" />
 <p className="mb-0 small text-muted">Click to upload or drag files here</p>
 <p className="smaller text-muted">Supports images, PDF, drawings</p>
 </label>
 </div>
 {createFiles.length > 0 && (
 <div className="mt-2 d-flex flex-wrap">
 {createFiles.map((file, i) => (
 <span key={i} className="badge bg-white text-dark border me-2 mb-2 p-2">
 <i className="fa fa-file me-1 text-primary" /> {file.name}
 </span>
 ))}
 </div>
 )}
 </div>
 </div>
 </Modal.Body>
 <Modal.Footer className="bg-white">
 <button type="button" className="btn btn-light px-4 rounded-pill" onClick={() => setShowCreate(false)}>Cancel</button>
 <button type="submit" className="btn btn-primary px-4 rounded-pill">Submit Ticket</button>
 </Modal.Footer>
 </form>
 </Modal>

 <Modal show={showDetails} onHide={() => setShowDetails(false)} size="xl" centered scrollable className="ticket-details-modal">
 <Modal.Header closeButton className="bg-white border-bottom">
 <Modal.Title>
 <div className="d-flex align-items-center">
 <span className="badge bg-primary-light text-primary me-2">{selectedTicket?.ticketNo}</span>
 <span className="text-dark fs-5">{selectedTicket?.title || selectedTicket?.category}</span>
 </div>
 </Modal.Title>
 </Modal.Header>
 <Modal.Body className="p-0">
 {selectedTicket && (
 <div className="row g-0 h-100">
 {/* Left Sidebar - Details */}
 <div className="col-xl-4 col-lg-5 border-end bg-white p-4 overflow-auto" style={{ maxHeight: '80vh' }}>
 <div className="mb-4">
 <label className="small text-muted text-uppercase fw-bold mb-2 d-block">Status & Priority</label>
 <div className="d-flex flex-wrap gap-2">
 <span className={badgeClass(selectedTicket.status)}>{selectedTicket.status}</span>
 <span className={badgeClass(selectedTicket.priority)}>{selectedTicket.priority}</span>
 </div>
 </div>

 <div className="mb-4">
 <label className="small text-muted text-uppercase fw-bold mb-2 d-block">Linked Project</label>
 <div className="bg-white p-3 rounded border shadow-xs">
 <div className="fw-bold text-dark">{selectedTicket.project?.projectName}</div>
 <div className="small text-muted mb-2">{selectedTicket.project?.projectCode}</div>
 <div className="small"><i className="fa fa-map-marker-alt text-primary me-2" />{selectedTicket.location || "N/A"}</div>
 </div>
 </div>

 {selectedTicket.workOrder && (
 <div className="mb-4">
 <label className="small text-muted text-uppercase fw-bold mb-2 d-block">Work Order</label>
 <div className="bg-white p-3 rounded border shadow-xs">
 <div className="small fw-bold">{selectedTicket.workOrder.woNo}</div>
 <div className="smaller text-muted">{selectedTicket.workOrder.subject}</div>
 </div>
 </div>
 )}

 <div className="mb-4">
 <label className="small text-muted text-uppercase fw-bold mb-2 d-block">Personnel</label>
 <div className="d-flex flex-column gap-3 bg-white p-3 rounded border shadow-xs">
 <div className="d-flex align-items-center">
 <div className="avatar-xs bg-info-light text-info rounded-circle me-3">CR</div>
 <div>
 <div className="smaller text-muted">Created By</div>
 <div className="small fw-bold">{selectedTicket.createdBy?.name}</div>
 </div>
 </div>
 {selectedTicket.assignedTo && (
 <div className="d-flex align-items-center">
 <div className="avatar-xs bg-success-light text-success rounded-circle me-3">EG</div>
 <div>
 <div className="smaller text-muted">Assigned Engineer</div>
 <div className="small fw-bold">{selectedTicket.assignedTo?.name}</div>
 </div>
 </div>
 )}
 </div>
 </div>

 {/* Integration Links */}
 <div className="mb-4">
 <label className="small text-muted text-uppercase fw-bold mb-2 d-block">Integrations</label>
 <div className="list-group list-group-flush border rounded bg-white">
 {selectedTicket.linkedRefs?.mir ? (
 <div className="list-group-item d-flex justify-content-between align-items-center py-2">
 <span className="small text-muted">MIR</span>
 <span className="small fw-bold">{selectedTicket.linkedRefs.mir.mirNumber}</span>
 </div>
 ) : null}
 {selectedTicket.linkedRefs?.drawing ? (
 <div className="list-group-item d-flex justify-content-between align-items-center py-2">
 <span className="small text-muted">Drawing</span>
 <span className="small fw-bold">{selectedTicket.linkedRefs.drawing.documentNo}</span>
 </div>
 ) : null}
 {selectedTicket.linkedRefs?.invoice ? (
 <div className="list-group-item d-flex justify-content-between align-items-center py-2">
 <span className="small text-muted">Invoice</span>
 <span className="small fw-bold">{selectedTicket.linkedRefs.invoice.invoiceNumber}</span>
 </div>
 ) : null}
 {selectedTicket.linkedRefs?.document ? (
 <div className="list-group-item d-flex justify-content-between align-items-center py-2">
 <span className="small text-muted">Document</span>
 <span className="small fw-bold">{selectedTicket.linkedRefs.document.title}</span>
 </div>
 ) : null}
 </div>
 {!selectedTicket.linkedRefs?.mir && !selectedTicket.linkedRefs?.drawing && !selectedTicket.linkedRefs?.invoice && !selectedTicket.linkedRefs?.document && (
 <div className="text-center p-3 border rounded bg-white small text-muted border-dashed">No linked records</div>
 )}
 {/* Linked QC Inspection */}
 {selectedTicket.linkedQC && (
 <div className="mt-3">
 <label className="small text-muted text-uppercase fw-bold mb-2 d-block">
 <i className="fas fa-microscope me-1 text-primary" /> Linked QC Inspection
 </label>
 <div className="border rounded bg-white p-3">
 <div className="d-flex justify-content-between align-items-start">
 <div>
 <div className="fw-bold text-primary small">{selectedTicket.linkedQC.qcNumber}</div>
 <div className="small text-muted mt-1">{selectedTicket.linkedQC.activityOrScope}</div>
 {selectedTicket.linkedQC.nonConformanceDetails && (
 <div className="small mt-1 text-danger">
 <i className="fas fa-exclamation-triangle me-1" />
 {selectedTicket.linkedQC.nonConformanceDetails}
 </div>
 )}
 </div>
 <div className="ms-2 text-end">
 <span className={`badge ${selectedTicket.linkedQC.ncrClosedDate ? "bg-success" : "bg-danger"}`}>
 NCR {selectedTicket.linkedQC.ncrClosedDate ? "Closed" : "Open"}
 </span>
 </div>
 </div>
 </div>
 </div>
 )}
 </div>
 </div>

 {/* Right Content - Description & Timeline */}
 <div className="col-xl-8 col-lg-7 p-4 overflow-auto" style={{ maxHeight: '80vh' }}>
 <div className="mb-4 bg-white p-4 rounded border">
 <h6 className="fw-bold text-uppercase small text-muted mb-3 border-bottom pb-2">Description</h6>
 <p className="mb-0 text-dark fs-6" style={{ whiteSpace: 'pre-wrap' }}>{selectedTicket.description}</p>
 </div>

 <div className="mb-4">
 <h6 className="fw-bold text-uppercase small text-muted mb-3 border-bottom pb-2">Ticket Attachments</h6>
 <div className="row g-2">
 {(selectedTicket.attachments || []).length ? selectedTicket.attachments.map((file, index) => (
 <div className="col-md-6" key={index}>
 <AttachmentItem file={file} index={index} />
 </div>
 )) : <div className="col-12 text-muted small fst-italic">No attachments uploaded with the ticket</div>}
 </div>
 </div>

 <div className="mb-4">
 <h6 className="fw-bold text-uppercase small text-muted mb-3 border-bottom pb-2">Activity Timeline</h6>
 <div className="timeline-widget p-3 bg-white">
 {(selectedTicket.actions || []).length ? (selectedTicket.actions || []).map((action, idx) => (
 <div key={action._id} className="timeline-item pb-4 border-start ps-4 position-relative">
 <div className="timeline-dot bg-white border border-primary position-absolute" style={{ left: '-8px', top: '0', width: '15px', height: '15px', borderRadius: '50%' }}></div>
 <div className="d-flex justify-content-between align-items-center mb-1">
 <span className="badge bg-light text-dark fw-bold small">{action.actionType}</span>
 <span className="smaller text-muted">{action.performedAt ? new Date(action.performedAt).toLocaleString() : ""}</span>
 </div>
 <div className="small fw-bold text-dark mb-1">{action.performedBy?.name}</div>
 {action.statusTo && (
 <div className="smaller mb-2">
 <span className="text-muted">Status:</span> <span className="text-info">{action.statusFrom || "-"}</span> → <span className="text-success">{action.statusTo}</span>
 </div>
 )}
 {action.comment && <p className="mb-2 text-dark small">{action.comment}</p>}
 {(action.attachments || []).length > 0 && (
 <div className="row g-2 mt-2">
 {action.attachments.map((file, i) => (
 <div className="col-md-6" key={i}>
 <AttachmentItem file={file} index={i} />
 </div>
 ))}
 </div>
 )}
 </div>
 )) : <p className="text-muted small">No activity recorded yet</p>}
 </div>
 </div>

 {/* Quick Actions at Bottom */}
 <div className="sticky-bottom bg-white pt-4 border-top mt-5">
 <div className="row g-3">
 {isManager && (
 <div className="col-md-12">
 <div className="bg-white p-3 rounded border">
 <label className="small fw-bold text-uppercase mb-2 d-block">Assign or Transfer Ticket</label>
 <div className="d-flex gap-2">
 <select className="form-select shadow-none border" value={assignUser} onChange={(e) => setAssignUser(e.target.value)}>
 <option value="">Select engineer</option>
 {engineers.map((user) => <option key={user._id} value={user._id}>{user.name} - {user.email}</option>)}
 </select>
 <button className="btn btn-primary btn-sm px-4" onClick={handleAssign}>Assign</button>
 </div>
 </div>
 </div>
 )}

 {(isEngineer || isManager) && (
 <div className="col-md-12">
 <div className="bg-white p-3 rounded border">
 <label className="small fw-bold text-uppercase mb-2 d-block">Submit Engineer Action</label>
 <form onSubmit={handleEngineerAction}>
 <div className="row g-2">
 <div className="col-md-4">
 <select className="form-select shadow-none border mb-2" value={actionForm.status} onChange={(e) => setActionForm({ ...actionForm, status: e.target.value })}>
 {ENGINEER_STATUSES.map((item) => <option key={item}>{item}</option>)}
 </select>
 <input className="form-control shadow-none border small" type="file" multiple onChange={(e) => setActionFiles(Array.from(e.target.files || []))} />
 </div>
 <div className="col-md-8">
 <textarea className="form-control shadow-none border mb-2" rows="3" placeholder="Explain the action taken or resolution..." value={actionForm.comment} onChange={(e) => setActionForm({ ...actionForm, comment: e.target.value })} />
 <div className="text-end">
 <button className="btn btn-success btn-sm px-4" type="submit">Submit Resolution Action</button>
 </div>
 </div>
 </div>
 </form>
 </div>
 </div>
 )}

 {isClient && (
 <div className="col-md-12">
 <div className="bg-white p-3 rounded border">
 <label className="small fw-bold text-uppercase mb-2 d-block">Client Verification & Feedback</label>
 <div className="row g-2">
 <div className="col-md-4">
 <select className="form-select shadow-none border mb-2" value={clientStatus} onChange={(e) => setClientStatus(e.target.value)}>
 {CLIENT_FINAL_STATUSES.map((item) => <option key={item}>{item}</option>)}
 </select>
 </div>
 <div className="col-md-8">
 <textarea className="form-control shadow-none border mb-2" rows="2" placeholder="Your feedback or reason for reopening..." value={clientComment} onChange={(e) => setClientComment(e.target.value)} />
 <div className="text-end">
 <button className="btn btn-primary btn-sm px-4" onClick={handleClientStatus}>Update Status</button>
 </div>
 </div>
 </div>
 </div>
 </div>
 )}
 </div>
 </div>
 </div>
 </div>
 )}
 </Modal.Body>
 </Modal>

 </Tab.Pane>

 {/* TAB 2: NCR Monitor */}
 <Tab.Pane eventKey="ncr">
 <div className="mt-2">
 <NCRMonitor />
 </div>
 </Tab.Pane>
 </Tab.Content>
 </Tab.Container>
 </>
 );
}

export default ClientTicketing;
